/* eslint-disable @typescript-eslint/no-explicit-any */
import { Avatar, Button, Dropdown, Image, TableColumnsType, Tag } from "antd";
import TableUi from "../../../atoms/StaticTable";
import { useRef, useState } from "react";
import { PopUp } from "../../../atoms/PopUp";
import EmptyCard from "../../../atoms/EmptyCard";
import { CrossIcon, DeleteIcon, NeedHelp } from "../../../atoms/CustomIcons";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import { MoreOutlined } from "@ant-design/icons";
import DashboardTemplate from "../../../templates/Dashboard";
import Header from "../../../atoms/Header";
import { getInitials } from "../../../config/utils";
import Filters from "./Components/Filters";
import taskTable from "../DummyData/taskTable.json";
import TaskModal from "./Components/TaskModal";

export default function Tasks() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord, setTableRecord] = useState<any | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string;
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
          }}
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          onClick={() => {
            setIsDeleteModalOpen(true);
          }}
          className="delete-hover"
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusClassMap: Record<string, string> = {
    "in progress": "approval_pending_tag",
    open: "grey_tag",
    completed: "accepted_tag",
    scheduled: "sent_tag",
    closed: "cancelled_tag",
  };

  const serviceLineClassMap: Record<string, string> = {
    "landscape maintenance": "green_tag",
    "lawn care": "orange_tag",
    irrigation: "violet_tag",
    construction: "yellow_tag",
    "Holiday lighting": "blue_tag",
    "Snow services": "red_tag",
  };

  const columns: TableColumnsType = [
    {
      title: "Tasks",
      dataIndex: "name",
      width: 320,
      render: (text) => text ?? "-",
      fixed: "left",
      sorter: (a, b) =>
        a.name?.localeCompare(b.name) || a.name?.length - b.name?.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 147,
      sorter: (a, b) =>
        a.status?.localeCompare(b.status) ||
        a.status?.length - b.status?.length,
      render: (text) => {
        const cls = statusClassMap[text?.toLowerCase()] || "default_tag";
        return (
          <Tag
            className={cls}
            style={{ textTransform: "capitalize", border: 0 }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 140,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.priority?.localeCompare(b.priority) ||
        a.priority?.length - b.priority?.length,
    },
    {
      title: "Due date",
      dataIndex: "due_date",
      width: 170,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.due_date?.localeCompare(b.due_date) ||
        a.due_date?.length - b.due_date?.length,
    },
    {
      title: "Occurrence",
      dataIndex: "occurrence",
      width: 140,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/images/work-orders/reverse_horizontal.svg" alt="icon" />
          {text ? text : "-"}
        </span>
      ),
      sorter: (a, b) =>
        a.occurrence?.localeCompare(b.occurrence) ||
        a.occurrence?.length - b.occurrence?.length,
    },
    {
      title: "Scheduled date",
      dataIndex: "schedule_date",
      width: 195,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.schedule_date?.localeCompare(b.schedule_date) ||
        a.schedule_date?.length - b.schedule_date?.length,
    },
    {
      title: "Property",
      dataIndex: "property",
      width: 270,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.property?.localeCompare(b.property) ||
        a.property?.length - b.property?.length,
    },
    {
      title: "Contract",
      dataIndex: "contract",
      width: 270,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.contract?.localeCompare(b.contract) ||
        a.contract?.length - b.contract?.length,
    },
    {
      title: "Service line",
      dataIndex: "service_line",
      width: 270,
      render: (text) => {
        const cls = serviceLineClassMap[text?.toLowerCase()] || "default_tag";
        return text ? <Tag className={cls}>{text}</Tag> : "-";
      },
      sorter: (a, b) =>
        a.service_line?.localeCompare(b.service_line) ||
        a.service_line?.length - b.service_line?.length,
    },
    {
      title: "Service",
      dataIndex: "service",
      width: 200,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.service?.localeCompare(b.service) ||
        a.service?.length - b.service?.length,
    },
    {
      title: "Crew",
      dataIndex: "crew",
      width: 180,
      render: (text) =>
        text ? <Tag className="default_tag">{text}</Tag> : "-",
      sorter: (a, b) =>
        a.crew?.localeCompare(b.crew) || a.crew?.length - b.crew?.length,
    },
    {
      title: "Production manager",
      dataIndex: "production_manager",
      width: 180,
      sorter: (a, b) =>
        a.production_manager?.name?.localeCompare(b.production_manager?.name) ||
        a.production_manager?.name?.length - b.production_manager?.name?.length,
      render: (text) =>
        text?.name ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {text?.image_url ? (
              <Avatar
                size={24}
                style={{ marginRight: 8 }}
                src={text?.image_url}
              />
            ) : (
              <Avatar size={24} style={{ marginRight: 8 }}>
                {getInitials(text?.name, "")}
              </Avatar>
            )}
            <span style={{ textTransform: "capitalize" }}>{text?.name}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              // setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          selectedRowKeys?.length > 0 ? (
            <>
              Are you sure you want to delete selected task?
              <br />
              This action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to delete <b>tableRecord?.name</b>?<br />
              <br />
              This action cannot be undone.
            </>
          )
        }
        btnText={
          selectedRowKeys?.length > 0
            ? `Delete ${
                selectedRowKeys?.length === 1
                  ? selectedRowKeys?.length + " task"
                  : selectedRowKeys?.length + " tasks"
              }`
            : "Delete task"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const taskDetailModal = {
    visibility: isTaskModalOpen,
    class: "Task_Modal",
    content: (
      <TaskModal
        onClose={() => setIsTaskModalOpen(false)}
        tableRecord={tableRecord}
        loading={loading}
      />
    ),
    width: 1200,
    onCancel: () => setIsTaskModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading='{tableRecord?.status === "active" ? "Archive" : "Activate"}'
        paragraph={
          <>
            Are you sure you want to{" "}
            {/* {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This task won’t be available for use.</>
            )} */}
          </>
        }
        btnText={`${
          "Activate"
          // tableRecord?.status === "active" ? "Archive" : "Activate"
        } task`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  const onClickRow = (record: any) => {
    return {
      onClick: () => {
        setTableRecord(record);
        setIsTaskModalOpen(true);
      },
    };
  };

  return (
    <DashboardTemplate loading={loading}>
      <Header
        title="Work orders & tasks"
        // description="View and manage your tasks and subcontractors."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />
      <TableUi
        columns={columns}
        data={{
          count: taskTable?.length,
          data: taskTable || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            companyId={companyId}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="New task"
        createButtonLink={`/new-task/${companyId}`}
        onClickRow={onClickRow}
      />
      {taskTable?.length === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived tasks"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} tasks`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived tasks here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "task" : "tasks"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
      <PopUp {...taskDetailModal} />
    </DashboardTemplate>
  );
}
